import React, { useState } from 'react';
import { Upload, Download, FileText, CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';

const BulkUpload = ({ onKernelsLoaded }) => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMode, setProcessingMode] = useState(null); // 'download' or 'llm'
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls')) {
        setFile(droppedFile);
        setError(null);
      } else {
        setError('Please upload a CSV or Excel file');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please upload a CSV or Excel file');
      }
    }
  };

  const handleUpload = async (mode = 'download') => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsProcessing(true);
    setProcessingMode(mode);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      
      if (mode === 'llm') {
        // Load kernels for LLM testing
        const response = await fetch(`${backendUrl}/api/bulk/upload-for-llm`, {
          method: 'POST',
          body: formData,
          headers: {
            'Cache-Control': 'no-cache'
          }
        });

        // Check content type before reading
        const contentType = response.headers.get("content-type");
        
        if (!response.ok) {
          // Try to parse error as JSON
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Upload failed');
          }
          throw new Error(`Upload failed: ${response.status}`);
        }
        
        // Parse JSON response
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error('Expected JSON response from server');
        }
        
        const data = await response.json();
        
        // Check if we got valid data
        if (data && data.success && data.kernels && data.kernels.length > 0) {
          // Pass kernels to parent component
          if (onKernelsLoaded) {
            onKernelsLoaded(data.kernels);
          }
          
          setResult({
            success: true,
            message: `✅ Loaded ${data.successful} charts successfully! Switch to "LLM Testing" tab to generate predictions.`,
            kernelCount: data.successful
          });
        } else {
          throw new Error('No valid kernels returned from server');
        }
      } else {
        // Download ZIP mode
        const response = await fetch(`${backendUrl}/api/bulk/upload`, {
          method: 'POST',
          body: formData,
        });

        // Check content type first
        const contentType = response.headers.get("content-type");
        
        if (!response.ok) {
          // For errors, try to read as JSON if content-type indicates it
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Upload failed');
          }
          throw new Error(`Upload failed: ${response.status}`);
        }

        // Expect ZIP file for download mode
        if (!contentType || !contentType.includes("application/zip")) {
          console.warn('Expected ZIP file, got:', contentType);
        }
        
        // Get the ZIP file blob
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulk_kernels_${new Date().getTime()}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setResult({
          success: true,
          message: 'Processing complete! Your ZIP file has been downloaded.',
          filename: a.download
        });
      }
      
      // Keep file and result for reference (don't auto-clear)
      // User can manually clear by uploading a new file

    } catch (err) {
      console.error('Bulk upload error:', err);
      setError(err.message || 'Failed to process bulk upload');
    } finally {
      setIsProcessing(false);
      setProcessingMode(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Bulk Chart Generation
        </h2>
        <p className="text-gray-600">
          Upload a CSV or Excel file with multiple birth entries and generate optimized charts for all of them at once.
        </p>
      </div>

      {/* Instructions Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          CSV/Excel Format Instructions
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p className="font-medium">Required columns:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Name</strong> - Full name of the person</li>
            <li><strong>Date of Birth</strong> - Format: "27 June 1988" or "1988-06-27"</li>
            <li><strong>Time of Birth</strong> - Format: "12:30" or "12:30 PM"</li>
            <li><strong>Place of Birth</strong> - City, State, Country</li>
          </ul>
          <p className="font-medium mt-3">Optional columns:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Gender</strong> - Male/Female</li>
            <li><strong>Rodden Rating</strong> - A/B/C/DD</li>
          </ul>
          <p className="mt-3 text-xs">
            Example: <code className="bg-blue-100 px-2 py-1 rounded">Name,Date of Birth,Time of Birth,Place of Birth</code>
          </p>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-12 text-center transition-all ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
        
        {!file ? (
          <div className="space-y-4">
            <Upload className="w-16 h-16 mx-auto text-gray-400" />
            <div>
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-blue-600 hover:text-blue-700 font-semibold"
              >
                Click to upload
              </label>
              <span className="text-gray-600"> or drag and drop</span>
            </div>
            <p className="text-sm text-gray-500">
              CSV or Excel files only (Max 100 entries recommended)
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <FileText className="w-16 h-16 mx-auto text-blue-600" />
            <div>
              <p className="font-semibold text-gray-900">{file.name}</p>
              <p className="text-sm text-gray-500">
                {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
            <button
              onClick={() => setFile(null)}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Remove file
            </button>
          </div>
        )}
      </div>

      {/* Process Buttons */}
      {file && (
        <div className="mt-6 space-y-3">
          <button
            onClick={() => handleUpload('download')}
            disabled={isProcessing}
            className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all ${
              isProcessing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {isProcessing && processingMode === 'download' ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Download className="w-5 h-5" />
                Generate & Download ZIP
              </span>
            )}
          </button>
          
          <button
            onClick={() => handleUpload('llm')}
            disabled={isProcessing}
            className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all ${
              isProcessing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {isProcessing && processingMode === 'llm' ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span className="text-xl">🤖</span>
                Load for LLM Testing
              </span>
            )}
          </button>
          
          <p className="text-xs text-center text-gray-500 mt-2">
            💡 Use "Load for LLM Testing" to test predictions with your LLM API (limit: 50 charts)
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {result && result.success && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-green-900">Success!</p>
              <p className="text-sm text-green-700">{result.message}</p>
              <p className="text-xs text-green-600 mt-1">
                File: {result.filename}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* What's Included Section */}
      <div className="mt-8 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
        <h3 className="font-bold text-gray-900 mb-4 text-lg">
          📦 What's Included in Each Chart?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Birth Data & Panchanga</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>D1 Chart & All Vargas (D1-D60)</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Bhava Chalit</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Jaimini System</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Upagrahas & Special Lagnas</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Yogi/Dagdha/Trimurti</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Strength Systems (Shadbala)</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>KP System</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-semibold">Dasha Systems (Corrected!)</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Varsaphala & Transits</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-semibold">Ashtakavarga (Complete BAV+SAV)</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>All 13 Sections - Nothing Missing!</span>
          </div>
        </div>
        <p className="mt-4 text-xs text-gray-600 italic">
          Each chart is ~83 KB in optimized JSON format - perfect for LLM processing!
        </p>
      </div>
    </div>
  );
};

export default BulkUpload;
